import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { DropdownItem } from '@atlaskit/dropdown-menu';
import Spinner from '@atlaskit/spinner/spinner';

import { useCommentsDataState } from '@confluence/comments-data/entry-points/useCommentsData';
import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags';
import { useCommentsPanelState } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import { fg } from '@confluence/feature-gating';
import { useUnreadCommentsState } from '@confluence/unread-comments/entry-points/useUnreadComments';

import { useCommentBulkRead } from '../hooks/useCommentBulkRead';
import { computeUnreadCommentIds } from '../helper/commentHelpers';
import { useCommentBulkOperationsState } from '../hooks/useCommentBulkOperations';
import { useCommentBulkOperationsFlags, READ } from '../hooks/useCommentBulkOperationsFlags';

const i18n = defineMessages({
	markAllAsRead: {
		id: 'comments-panel.comment-bulk-actions.mark-all-as-read',
		defaultMessage: 'Mark all as read',
		description:
			'The label for a button used to "mark as read" all comments associated with the content being viewed',
	},
});

export const CommentBulkRead = ({ flags }: WithFlagsProps) => {
	const { formatMessage } = useIntl();
	const pageMode = useGetPageMode();
	const includeDrafts = pageMode !== PageMode.VIEW;
	const { retryCountRef, ...commentBulkActionFlags } = useCommentBulkOperationsFlags(READ, flags);
	const { commentsMarkedAsRead } = useCommentsPanelState();

	const { unreadCommentIDSet } = useUnreadCommentsState();

	const commentBulkRead = useCommentBulkRead(commentBulkActionFlags);
	const { commentsDataMap } = useCommentsDataState();
	const { isBulkReadInProgress } = useCommentBulkOperationsState();
	const unreadCommentIdCount = fg('confluence_frontend_unread_comments_fixes')
		? unreadCommentIDSet.size
		: computeUnreadCommentIds(commentsDataMap, commentsMarkedAsRead, includeDrafts).size;

	return (
		<DropdownItem
			elemBefore={isBulkReadInProgress && <Spinner size="small" />}
			isDisabled={isBulkReadInProgress || unreadCommentIdCount === 0}
			onClick={() => {
				retryCountRef.current = 0;

				void commentBulkRead();
			}}
		>
			{formatMessage(i18n.markAllAsRead)}
		</DropdownItem>
	);
};

export const CommentBulkReadWithFlags = withFlags(CommentBulkRead);
